import { useQuery } from '@tanstack/react-query';
import { Card, StatusChip } from '@smena/ui';
import { QueryState } from '@/components/QueryState';
import { fetchAdminAudit, fetchAuditLog, type AdminAuditItem, type AuditLogItem } from './api';

// Два журнала рядом:
//   audit_log   - что делали агент / операторы клиента / система (виден и клиенту в его панели);
//   admin_audit - что делали МЫ, включая просмотры чужих переписок (только здесь).
// Разделение принципиальное: смешать их - потерять ответ на вопрос «кто из нас смотрел ПДн».

export function AuditTable({
  scope,
  accountId,
  action,
  limit = 100,
}: {
  scope: 'account' | 'admin';
  accountId?: string;
  action?: string;
  limit?: number;
}) {
  const q = useQuery<Array<AuditLogItem | AdminAuditItem>>({
    queryKey: ['audit', scope, accountId ?? null, action ?? null, limit],
    queryFn: () => {
      const filter = {
        ...(accountId ? { account: accountId } : {}),
        ...(action ? { action } : {}),
        limit,
      };
      return scope === 'admin' ? fetchAdminAudit(filter) : fetchAuditLog(filter);
    },
  });

  return (
    <Card className="overflow-hidden">
      <QueryState
        isLoading={q.isLoading}
        error={q.error}
        isEmpty={q.data?.length === 0}
        emptyText="Записей нет"
      >
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-xs uppercase tracking-wider text-muted">
            <tr>
              <th className="px-4 py-2 font-medium">Время</th>
              <th className="px-4 py-2 font-medium">{scope === 'admin' ? 'Админ' : 'Актор'}</th>
              <th className="px-4 py-2 font-medium">Действие</th>
              <th className="px-4 py-2 font-medium">Аккаунт</th>
              <th className="px-4 py-2 font-medium">Payload</th>
            </tr>
          </thead>
          <tbody>
            {q.data?.map((r) => {
              const isAdmin = 'adminUserEmail' in r;
              return (
                <tr key={r.id} className="border-b border-line/60 align-top last:border-0">
                  <td className="whitespace-nowrap px-4 py-2 text-xs text-muted">
                    {new Date(r.ts).toLocaleString('ru-RU')}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">
                    {isAdmin ? (r.adminUserEmail ?? 'удалён') : r.actor}
                  </td>
                  <td className="px-4 py-2">
                    <StatusChip tone={actionTone(r.action)}>{r.action}</StatusChip>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-muted">
                    {(isAdmin ? r.targetAccountSlug : r.accountSlug) ?? '-'}
                  </td>
                  <td className="max-w-md px-4 py-2 font-mono text-[11px] text-muted">
                    <span className="line-clamp-2 break-all">{JSON.stringify(r.payload)}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </QueryState>
    </Card>
  );
}

/** Просмотры ПДн подсвечиваем отдельно - их должно быть видно с одного взгляда на журнал. */
function actionTone(action: string) {
  if (action.endsWith('_view') || action === 'account.view') return 'amber' as const;
  if (action.includes('error') || action.includes('fail')) return 'red' as const;
  if (action.startsWith('account.') || action.startsWith('staff_user.')) return 'accent' as const;
  return 'muted' as const;
}
